import { format } from 'date-fns';
import { useUsername } from '../../../../hooks/useUsername';
import VyTooltip from '../../../common/VyTooltip';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { linkify } from '../../../../utils/functions';
import { MessageOptions } from './MessageOptions';
import AttendeeAvatar from '../../templates/VystemLight/AttendeeAvatar';
import { MeetingChatMessage } from '../../../../interfaces/interfaces';
import { useEffect, useState } from 'react';
import { VYSTEMBLUE } from '../../../../static/constants';

export interface MessageStyle {
  avatar?: {
    background?: string;
  };
}

export interface MessageProps {
  message: MeetingChatMessage;
  enableHighlights: boolean;
  style?: MessageStyle;
}

export const Message = ({ message, enableHighlights, style }: MessageProps) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);

  const [isOwnMessage, setIsOwnMessage] = useState(false);
  const [messageStyle, setMessageStyle] = useState<{
    messageColors: {
      color: string | undefined;
      backgroundColor: string | undefined;
    };
    replyToColors: { color: string | undefined };
  }>();

  const { getConstructedName } = useUsername();

  function determineMessageColors() {
    if (!!event) {
      setMessageStyle({
        messageColors: {
          color: isOwnMessage ? event?.template.mainButtonTextColor : event?.template.complementButtonTextColor,
          backgroundColor: isOwnMessage ? event?.template.mainColor : event?.template.complementColor,
        },
        replyToColors: { color: isOwnMessage ? event?.template.mainButtonTextColor : event?.template.complementButtonTextColor },
      });
    } else {
      setMessageStyle({
        messageColors: {
          color: 'white',
          backgroundColor: isOwnMessage ? VYSTEMBLUE : 'gray',
        },
        replyToColors: { color: 'white' },
      });
    }
  }

  useEffect(() => {
    determineMessageColors();
  }, [isOwnMessage]);

  useEffect(() => {
    setIsOwnMessage(user?._id === message.user._id);
  }, [event, user, message]);

  return (
    <div key={message._id} className={`flex items-start gap-2.5 relative ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
      <div className="mt-1 w-10">
        <AttendeeAvatar
          attendee={{ ...message.user, backgroundColor: style?.avatar?.background }}
          showStatus
          reportMessage={message}
          size="medium"
          isProfileModalOnly
        />
      </div>

      <div className={`flex flex-col max-w-[320px]`}>
        <div className={`flex items-center ${isOwnMessage ? 'self-end flex-row' : 'self-start flex-row-reverse'} gap-2 text-slate-400`}>
          <span className="text-sm break-normal break-words">{getConstructedName(message.user)}</span>
          <span>-</span>
          <VyTooltip title={format(new Date(message.datetime_created), 'dd.MM.yyyy HH:mm:ss')}>
            <span className="font-mono text-xs">{format(new Date(message.datetime_created), 'HH:mm')}</span>
          </VyTooltip>
        </div>
        <div className={`flex items-end ${isOwnMessage ? 'flex-row' : 'flex-row-reverse'} ${isOwnMessage ? 'self-end' : 'self-start'}`}>
          <MessageOptions message={message} enableHighlights={enableHighlights} />
          <div
            className={`flex flex-col items-center max-w-full rounded-xl ${message.isHighlighted ? 'border-yellow-500 border-4' : ''}  ${
              isOwnMessage ? 'rounded-tr-none' : 'rounded-tl-none'
            }`}
            style={messageStyle?.messageColors}>
            {message.replyTo && (
              <div className={`p-2 backdrop-brightness-75 rounded-lg m-2 mb-0 w-11/12`} style={messageStyle?.replyToColors}>
                <p className="text-sm font-bold">{getConstructedName(message.replyTo.user)}</p>
                <p className="text-sm break-normal hyphens-auto">{message.replyTo.message}</p>
              </div>
            )}
            <div
              className={`flex flex-col px-4 rounded-xl w-full ${isOwnMessage ? 'rounded-tr-none' : 'rounded-tl-none'} ${
                message.replyTo && isOwnMessage ? 'text-right' : ''
              }`}
              style={messageStyle?.messageColors}>
              <p
                dangerouslySetInnerHTML={{ __html: linkify(message.message, 'underline') }}
                className="text-sm font-normal py-2.5 dark:text-white break-normal hyphens-auto"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
